import { events } from "@constants/tracking";
import { useSessionConfigDialog } from "@contexts/DialogContext/dialogs/SessionConfigDialog";
import { useExamsFilterDialog } from "@contexts/DialogContext/dialogs/exams-filter";
import { useThemeContext } from "@contexts/ThemeContext";
import { getOppositeThemePreference } from "@contexts/theme-preference";
import { buildCommandBarActions } from "@features/command-bar/helpers";
import { useExamContext } from "@features/exam/contexts/ExamContext";
import useMultipleExams from "@features/exam/contexts/useMultipleExams";
import { getShowStudyPackViews } from "@helpers/exams";
import { useAppStore } from "@hooks/useAppStore";
import { isWeb } from "@memorang/helpers/platform";
import { trackCustomEvent } from "analytics";
import { type Href, useRouter } from "expo-router";
import { useRegisterActions } from "kbar";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

export const useCommandBarActions = () => {
	const router = useRouter();
	const { isDarkMode, setThemePreference } = useThemeContext();
	const navigationRoutes = useAppStore((s) => s.navigation?.routes.main);
	const hideStudyPack = useAppStore((s) => s.navigation?.hideStudyPack);
	const showStudyPackAsHome = useAppStore((s) => s.showStudyPackAsHome);
	const store = useAppStore((s) => s.store);
	const showRedeem = useAppStore((s) => s.homeView?.showRedeem);
	const practiceTestLabel = useAppStore((s) => s.exam.practiceTest?.label);

	const practiceTestEnabled = useAppStore((s) => s.exam.practiceTest?.enabled);
	const {
		currentExamContext: { studyPackCollectionId, practiceTestProductNumericId },
	} = useExamContext();
	const { hasMultipleExams } = useMultipleExams();
	const allowChangeExam = useAppStore((s) => s.exam.allowChangeExam);
	const { showExamsFilter } = useExamsFilterDialog();
	const { showSessionConfig } = useSessionConfigDialog();
	const { t } = useTranslation();

	const showStudyPackViews = getShowStudyPackViews(studyPackCollectionId);

	const onNavigate = useCallback(
		(path: Href) => {
			router.push(path);
		},
		[router],
	);

	const onTrack = useCallback((option: string) => {
		trackCustomEvent({
			eventName: events.commandBarOptionClicked,
			option,
		});
	}, []);

	const onShowExamsFilter = useCallback(() => {
		showExamsFilter();
	}, [showExamsFilter]);

	const onShowSessionConfig = useCallback(() => {
		showSessionConfig({});
	}, [showSessionConfig]);

	const onToggleTheme = useCallback(() => {
		setThemePreference(getOppositeThemePreference(isDarkMode));
	}, [isDarkMode, setThemePreference]);

	const actions = useMemo(
		() =>
			buildCommandBarActions({
				navigationRoutes: navigationRoutes ?? [],
				showStudyPackAsHome: !!showStudyPackAsHome,
				studyPackCollectionId,
				showStudyPackViews: !hideStudyPack && showStudyPackViews,
				showQuickStudyAction: showStudyPackViews,
				practiceTestProductNumericId,
				practiceTestEnabled,
				practiceTestLabel,
				storeEnabled: store.enabled,
				showRedeem,
				t,
				isWebPlatform: isWeb,
				isDarkMode,
				showChangeExam: Boolean(allowChangeExam) && hasMultipleExams,
				onNavigate,
				onTrack,
				onShowExamsFilter,
				onShowSessionConfig,
				onToggleTheme,
			}),
		[
			navigationRoutes,
			showStudyPackAsHome,
			studyPackCollectionId,
			showStudyPackViews,
			hideStudyPack,
			practiceTestProductNumericId,
			practiceTestEnabled,
			practiceTestLabel,
			store.enabled,
			showRedeem,
			t,
			isDarkMode,
			hasMultipleExams,
			onNavigate,
			onTrack,
			onShowExamsFilter,
			onShowSessionConfig,
			allowChangeExam,
			onToggleTheme,
		],
	);

	useRegisterActions(actions, [actions]);
};
